import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/shadcn/ui/button';
import { Switch } from '@/components/shadcn/ui/switch';
import {
  type PeriodicFormData,
  periodicFormDefaultValues,
  periodicFormOptions,
  useCreatePeriodicMutation,
  useUpdatePeriodicMutation,
} from '@/entities/(notification)/periodic';
import { enableNotification } from '@/entities/notification';
import { errorHandler } from '@/error-handler';
import { PeriodicFormContent } from '@/features/Workflow/periodic';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useAppForm } from '@/shared/ui/form';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import {
  WORKFLOW_CHANNEL_TYPE,
  WORKFLOW_PERIODIC_TRIGGER_TYPE,
  WORKFLOW_TYPE,
} from '@/types/workflowConst';
import { deleteObjectField } from '@/utils/object';

import useWorkflowDetail from '../useNotiDetail';

type Props = {
  initialState?: PeriodicWorkflow;
  mode: EDIT_MODE;
};

const PeriodicWorkflowDetail = ({ initialState, mode }: Props) => {
  const wr = useWorkspaceRouter();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceId = currWorkspace?.alias ?? '';
  const [enable, setEnable] = useState(initialState?.enable ?? true);

  const createMutation = useCreatePeriodicMutation();
  const updateMutation = useUpdatePeriodicMutation();

  const { onDelete } = useWorkflowDetail({
    mode,
    alias: initialState?.alias,
    initChange() {
      // no-op
    },
  });

  // Workflow 데이터를 form 데이터로 변환
  const getDefaultValues = (): PeriodicFormData => {
    if (!initialState) return periodicFormDefaultValues;

    const { periodic, channel } = initialState;
    // WorkflowChannel을 form schema의 channel 타입으로 변환
    const formChannel: PeriodicFormData['channel'] =
      channel?.type === WORKFLOW_CHANNEL_TYPE.SLACK
        ? {
            type: WORKFLOW_CHANNEL_TYPE.SLACK,
            slack_webhook_id: channel.slack_webhook_id ?? '',
          }
        : channel?.type === WORKFLOW_CHANNEL_TYPE.MAIL
          ? {
              type: WORKFLOW_CHANNEL_TYPE.MAIL,
              mail_address: channel.mail_address ?? '',
            }
          : { type: WORKFLOW_CHANNEL_TYPE.SLACK, slack_webhook_id: '' };

    return {
      title: initialState.title,
      channel: formChannel,
      basis: periodic.basis,
      all: periodic.all,
      basis_list: periodic.basis_list ?? [],
      timezone: periodic.timezone,
      schedule: periodic.schedule,
      triggerType:
        periodic.percentage !== undefined
          ? WORKFLOW_PERIODIC_TRIGGER_TYPE.PERCENTAGE
          : WORKFLOW_PERIODIC_TRIGGER_TYPE.NUMERICAL,
      percentage: periodic.percentage,
      numerical: periodic.numerical,
      delta: periodic.delta,
    };
  };

  // 저장 핸들러
  const handleSave = async (values: PeriodicFormData) => {
    // Build periodic params (triggerType은 UI-only 필드이므로 제외)
    let periodicParams = {
      basis: values.basis,
      all: values.all,
      basis_list: values.basis_list,
      timezone: values.timezone,
      schedule: values.schedule,
      percentage: values.percentage,
      numerical: values.numerical,
      delta: values.delta,
    };

    // numerical만 사용하는 경우 percentage 제거
    if (values.percentage === undefined) {
      periodicParams = deleteObjectField(periodicParams, 'percentage');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      title: values.title,
      enable,
      type: WORKFLOW_TYPE.PERIODIC,
      channel: values.channel,
      periodic: periodicParams,
    };

    if (mode === 'CREATE') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast('Workflow created', 'success');
          wr.push('/workflow');
        },
        onError: (error) => {
          toast(errorHandler(error, 'Failed to create workflow'), 'fail');
        },
      });
    } else {
      updateMutation.mutate(
        {
          notification_id: initialState!.alias,
          ...payload,
        },
        {
          onSuccess: () => {
            toast('Workflow updated', 'success');
            wr.push('/workflow');
          },
          onError: (error) => {
            toast(errorHandler(error, 'Failed to update workflow'), 'fail');
          },
        },
      );
    }
  };

  const form = useAppForm({
    ...periodicFormOptions,
    defaultValues: getDefaultValues(),
    onSubmit: async ({ value }) => {
      await handleSave(value);
    },
  });

  // 토글 핸들러
  const handleToggle = async (status: boolean) => {
    if (!initialState) return;
    try {
      await enableNotification({
        notification_id: initialState.alias,
        enable: status,
      });
      setEnable(status);
      toast(status ? 'Workflow enabled' : 'Workflow disabled', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to toggle workflow'), 'fail');
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden max-w-[1400px]">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-2 mb-6">
        <Button
          variant="outline"
          size="icon"
          onClick={() => wr.push('/workflow')}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-6">
          {mode === 'EDIT' && (
            <>
              <Switch checked={enable} onCheckedChange={handleToggle} />
              <Button variant="ghost" size="icon" onClick={onDelete}>
                <Trash2 className="size-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 본문 */}
      <PeriodicFormContent
        form={form}
        workspaceId={workspaceId}
        renderFooter={() => (
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <div className="flex gap-2">
                <Button
                  className="w-[100px]"
                  onClick={form.handleSubmit}
                  disabled={!canSubmit || isSubmitting}
                >
                  Save
                </Button>
              </div>
            )}
          </form.Subscribe>
        )}
      />
    </div>
  );
};

export default PeriodicWorkflowDetail;
